import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import apiClient from '../services/api';

interface Employee {
  id: string;
  employee_id: string;
  name: string;
  email: string;
  phone: string;
  department: string;
  designation: string;
  coins_allocated: number;
  coins_used: number;
  coins_balance: number;
  activity_level: string;
  bookings_count: number;
  last_activity: string | null;
  status: string;
}

interface CorporateAnalytics {
  summary: {
    total_employees: number;
    active_employees: number;
    coins_loaded: number;
    coins_used: number;
    utilization_percentage: number;
    avg_bookings_per_employee: number;
    total_bookings: number;
  };
  activity_breakdown: Record<string, { bookings: number; percentage: number }>;
  department_analysis: Array<{
    department: string;
    employees: number;
    active_employees: number;
    utilization_percentage: number;
    avg_bookings_per_employee: number;
  }>;
  health_metrics: {
    avg_steps_per_day: number;
    avg_workout_duration: number;
    avg_heart_rate: number;
    avg_sleep_hours: number;
  };
  trends: {
    utilization_trend: Array<{ week: string; percentage: number }>;
  };
}

interface CorporateEmployeeState {
  employees: Employee[];
  selectedEmployee: Employee | null;
  analytics: CorporateAnalytics | null;
  departments: string[];
  isLoading: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  limit: number;
}

const initialState: CorporateEmployeeState = {
  employees: [],
  selectedEmployee: null,
  analytics: null,
  departments: [],
  isLoading: false,
  error: null,
  totalCount: 0,
  page: 1,
  limit: 10
};

export const fetchEmployees = createAsyncThunk(
  'corporateEmployee/fetchEmployees',
  async ({ department, status, activity_level, page, limit }: { department?: string; status?: string; activity_level?: string; page?: number; limit?: number }, { rejectWithValue }) => {
    try {
      const params: any = { page: page || 1, limit: limit || 10 };
      if (department) params.department = department;
      if (status) params.status = status;
      if (activity_level) params.activity_level = activity_level;

      const response = await apiClient.get('/corporate/employees', { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch employees');
    }
  }
);

export const fetchEmployee = createAsyncThunk(
  'corporateEmployee/fetchEmployee',
  async (employeeId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/corporate/employees/${employeeId}`);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch employee');
    }
  }
);

export const createEmployee = createAsyncThunk(
  'corporateEmployee/createEmployee',
  async (employeeData: any, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/corporate/employees', employeeData);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create employee');
    }
  }
);

export const bulkUploadEmployees = createAsyncThunk(
  'corporateEmployee/bulkUpload',
  async ({ csv_data, coins_per_employee, expiry_date }: { csv_data: string; coins_per_employee: number; expiry_date?: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/corporate/employees/bulk-upload', {
        csv_data,
        coins_per_employee,
        expiry_date
      });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to upload employees');
    }
  }
);

export const updateEmployee = createAsyncThunk(
  'corporateEmployee/updateEmployee',
  async ({ employeeId, updates }: { employeeId: string; updates: any }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/corporate/employees/${employeeId}`, updates);
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update employee');
    }
  }
);

export const loadCoins = createAsyncThunk(
  'corporateEmployee/loadCoins',
  async ({ employeeId, coins, expiry_date }: { employeeId: string; coins: number; expiry_date?: string }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/corporate/employees/${employeeId}/load-coins`, {
        coins,
        expiry_date
      });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to load coins');
    }
  }
);

export const fetchCorporateAnalytics = createAsyncThunk(
  'corporateEmployee/fetchAnalytics',
  async ({ period, date_from, date_to }: { period?: string; date_from?: string; date_to?: string }, { rejectWithValue }) => {
    try {
      const params: any = {};
      if (period) params.period = period;
      if (date_from) params.date_from = date_from;
      if (date_to) params.date_to = date_to;

      const response = await apiClient.get('/corporate/analytics', { params });
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

export const fetchDepartments = createAsyncThunk(
  'corporateEmployee/fetchDepartments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/corporate/departments');
      return (response as any).data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch departments');
    }
  }
);

const corporateEmployeeSlice = createSlice({
  name: 'corporateEmployee',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    clearSelectedEmployee: (state) => {
      state.selectedEmployee = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.isLoading = false;
        state.employees = action.payload.employees;
        state.totalCount = action.payload.total_count;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEmployee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEmployee.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedEmployee = action.payload;
      })
      .addCase(fetchEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createEmployee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createEmployee.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(createEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(bulkUploadEmployees.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(bulkUploadEmployees.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(bulkUploadEmployees.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateEmployee.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateEmployee.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(updateEmployee.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(loadCoins.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loadCoins.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(loadCoins.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCorporateAnalytics.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCorporateAnalytics.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics = action.payload;
      })
      .addCase(fetchCorporateAnalytics.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.departments = action.payload;
      });
  }
});

export const { clearError, setPage, clearSelectedEmployee } = corporateEmployeeSlice.actions;
export default corporateEmployeeSlice.reducer;
